/**
 * DAS SCHWARZE BRETT (NT-SCHWARZES-BRETT-01) — Dashboard-Tab unter /brett (seit NT-LAYOUT-01
 * im Content-Bereich der App-Shell statt als eigenständige Seite), ursprünglich Muster wie
 * `pages/kladde.ts`: eigener Nav-Eintrag (Desktop-Sidebar + Hamburger-Drawer ACCOUNT-Gruppe),
 * kein Modal, kein Panel innerhalb der Optionen mehr (Chris-Korrektur — sollte gleichrangig
 * neben KLADDE stehen, nicht darin versteckt sein).
 *
 * Die eigentliche Board-/Formular-Logik lebt unverändert in components/SchwarzesBrettView.ts
 * (prepare/renderShell/mount/reset-Quartett) — diese Seite ist nur die Hülle (Titel, Zurück-
 * Button), exakt wie kladde.ts nur die Hülle um kladde-render.ts ist.
 */

import {
  prepareSchwarzesBrettView,
  resetSchwarzesBrettViewState,
  renderSchwarzesBrettViewShell,
  mountSchwarzesBrettView,
} from '../components/SchwarzesBrettView.js';

// Eigene Style-Injection (Muster: kladde.ts/settings.ts) — .bugreport-*/.settings-btn/
// .settings-hint/.settings-meta sind hier dupliziert (dieselben Klassennamen, die
// SchwarzesBrettView.ts bereits im Markup nutzt), nicht in settings.ts belassen — das
// „kein Shared-Import für reine Anzeige-/CSS-Werte"-Pattern gilt genauso für page-lokales CSS.
function addStyles(): void {
  const existing = document.getElementById('brett-styles');
  const el = existing ?? document.createElement('style');
  el.id = 'brett-styles';
  el.textContent = `
    .brett-page { max-width: 700px; margin: 0 auto; font-family: 'Share Tech Mono', 'Courier New', monospace; color: var(--text-primary); }
    .brett-page-title { font-size: var(--font-size-xl); text-transform: uppercase; color: var(--text-primary); margin-bottom: 2rem; letter-spacing: 0.08em; }

    .settings-meta { font-size: var(--font-size-xs); color: var(--text-muted); margin-bottom: 1rem; }
    .settings-btn {
      background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-primary);
      font-family: inherit; font-size: var(--font-size-xs); text-transform: uppercase;
      padding: 0.5rem 1.2rem; border-radius: var(--radius-btn); cursor: pointer; letter-spacing: 0.05em;
    }
    .settings-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .settings-btn:not(:disabled):hover { border-color: var(--text-muted); }
    .settings-hint { font-size: var(--font-size-xs); color: var(--text-muted); margin-top: 0.75rem; line-height: 1.5; }

    .bugreport-field { margin-bottom: 1rem; }
    .bugreport-label { display: block; font-size: var(--font-size-xs); text-transform: uppercase; color: var(--text-muted); letter-spacing: 0.05em; margin-bottom: 0.4rem; }
    .bugreport-input, .bugreport-select, .bugreport-textarea {
      display: block; width: 100%; max-width: 480px; background: var(--bg-elevated);
      border: 1px solid var(--border); color: var(--text-primary); font-family: inherit;
      font-size: var(--font-size-sm); padding: 0.5rem 0.75rem; border-radius: var(--radius);
    }
    .bugreport-input:focus, .bugreport-select:focus, .bugreport-textarea:focus { outline: none; border-color: var(--accent-gold); }
    .bugreport-textarea { resize: vertical; min-height: 80px; }
    .bugreport-honeypot { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }

    /* Das Schwarze Brett — Board/Karten (aus settings.ts hierher übernommen) */
    .brett-intro { margin-bottom: 1.5rem; }
    .brett-status-header {
      font-size: var(--font-size-xs); text-transform: uppercase; letter-spacing: 0.08em;
      color: var(--accent-gold); margin: 1.5rem 0 0.5rem; padding-bottom: 0.25rem;
      border-bottom: 1px solid var(--border);
    }
    .brett-status-header:first-child { margin-top: 0; }
    .brett-card {
      display: flex; align-items: center; gap: 0.75rem; background: var(--bg-surface);
      border: 1px solid var(--border); border-radius: var(--radius); padding: 0.75rem 1rem;
      margin-bottom: 0.5rem; cursor: pointer;
    }
    .brett-card:hover { border-color: var(--text-muted); }
    .brett-card-head { display: flex; flex-direction: column; gap: 0.15rem; flex: 1; min-width: 0; }
    .brett-card-number { font-size: var(--font-size-xs); color: var(--text-muted); }
    .brett-card-title {
      min-width: 0; color: var(--text-primary); font-size: var(--font-size-sm);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .brett-vote-btn {
      flex: 0 0 auto; background: none; border: 1px solid var(--border); color: var(--text-muted);
      font-family: inherit; font-size: var(--font-size-xs); padding: 0.35rem 0.6rem;
      border-radius: var(--radius-btn); cursor: pointer; white-space: nowrap;
    }
    .brett-vote-btn:hover { border-color: var(--accent-gold); color: var(--accent-gold); }
    .brett-vote-btn--active { border-color: var(--accent-gold); color: var(--accent-gold); background: rgba(240, 180, 41, 0.1); }
    .brett-vote-btn:disabled { opacity: 0.5; cursor: default; }
    .brett-detail-title { color: var(--text-primary); font-size: var(--font-size-lg); margin: 0.5rem 0 0.75rem; }
    .brett-detail-body { color: var(--text-primary); font-size: var(--font-size-sm); line-height: 1.6; white-space: pre-wrap; }
    @media (max-width: 480px) {
      .brett-card-title { white-space: normal; }
    }
  `;
  if (!existing) document.head.appendChild(el);
}

// NT-LAYOUT-01 (L5): Das Brett ist ein Dashboard-Tab (Content-Bereich, App-Shell bleibt
// stehen) statt einer eigenständigen Seite — kein eigener Zurück-Button mehr.

/** Wird vom Dashboard beim Wechsel AUF den Brett-Tab aufgerufen. */
export function prepareBrettTab(): void {
  prepareSchwarzesBrettView();
}

/** Beim Verlassen des Tabs bzw. Unmount des Dashboards. */
export function resetBrettTab(): void {
  resetSchwarzesBrettViewState();
}

export function renderBrettTab(): string {
  addStyles();
  return `
    <section class="dash-content">
      <div class="brett-page">
        <h1 class="brett-page-title">Das Schwarze Brett</h1>
        ${renderSchwarzesBrettViewShell()}
      </div>
    </section>
  `;
}

export async function mountBrettTab(): Promise<void> {
  await mountSchwarzesBrettView();
}
